<script setup lang="ts">
import { ref, computed } from 'vue';
import { storeToRefs, StoreGeneric } from 'pinia';
import {
  Grid, GridColumnProps,
} from '@progress/kendo-vue-grid';
import formatDate from '@/lib/format-date';
import AuthenticationTrustedDevice from '@/interfaces/AuthenticationTrustedDevice';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import useTFAuthenticationDeviceStore from '@/stores/tf-authentication-devices';
import useDeviceStore from '@/stores/device';
const deviceStore = useDeviceStore();
const authenticationDevices = useTFAuthenticationDeviceStore();
await authenticationDevices.initStore();

const { trustedDevices } = storeToRefs<StoreGeneric>(authenticationDevices);

const rememberedDeviceList = computed(() => trustedDevices.value.map((device: AuthenticationTrustedDevice) => ({
  ...device,
  controlsVisible: ref(false),
  isCurrentDevice: device.uniqueIdentifier === deviceStore.deviceID,
})));

const gridData = ref(rememberedDeviceList);

const columns = computed(() => [
  {
    field: null,
    title: null,
    width: '50px',
    cell: 'IconTemplate',
  },
  {
    field: 'deviceName',
    title: 'Device',
    width: '270px',
    cell: 'DeviceTemplate'
  },
  {
    field: 'browserName',
    title: 'Browser',
    cell: 'BrowserTemplate',
  },
  {
    field: 'trustedFrom',
    title: 'Trusted From',
    cell: 'TrustedFromTemplate',
  },
  {
    field: 'trustedTo',
    title: 'Trusted To',
    cell: 'TrustedToTemplate',
  },
  {
    field: 'lastLoginDate',
    title: 'Last login',
    cell: 'LoginTemplate',
  },
  {
    field: null,
    title: null,
    width: '90px',
    cell: 'RemoveTemplate',
  },
] as GridColumnProps[]);

</script>

<template>
  <div
    v-show="rememberedDeviceList.length"
    class="tw-mt-8"
  >
    <h3 class="tw-text-xl">
      Trusted Devices
    </h3>
    <p class="tw-mt-4">
      These are your trusted devices (based on browser cookies),
      you do not need to enter the verification code when logging in from these devices before the 'Trusted To' date.
    </p>
    <p class="tw-mb-4">
      If you lose your device,
      click the 'Remove' button immediately for the device and contact your internal ConnX administrator.
    </p>

    <div class="tw-hidden md:tw-block">
      <grid
        ref="grid"
        scrollable="none"
        :data-items="gridData"
        :row-class="() => 'tw-group'"
        :columns="columns"
      >
        <template #IconTemplate="{ props }">
          <td>
            <connx-icon
              v-if="props.dataItem.isDesktopDevice"
              name="far fa-laptop"
              class="tw-w-6 tw-text-connx-grey-shade-20"
            />
            <connx-icon
              v-else
              class="tw-w-4 tw-text-connx-grey-shade-20"
              name="fas fa-mobile-alt"
            />
          </td>
        </template>
        <template #DeviceTemplate="{ props }">
          <td>
            {{ props.dataItem.deviceName }}

            <span
              v-if="props.dataItem.isDeviceExpired"
              class="
                tw-text-connx-danger
              "
            >
              (Expired)
            </span>
            <span
              v-else-if="props.dataItem.isCurrentDevice"
              class="tw-text-connx-primary"
            >
              (your current session)
            </span>
          </td>
        </template>
        <template #BrowserTemplate="{ props }">
          <td>{{ props.dataItem.browserName }}</td>
        </template>
        <template #TrustedFromTemplate="{ props }">
          <td>{{ formatDate(props.dataItem.trustedFrom, true) }}</td>
        </template>
        <template #TrustedToTemplate="{ props }">
          <td>{{ formatDate(props.dataItem.trustedTo, true) }}</td>
        </template>
        <template #LoginTemplate="{ props }">
          <td>{{ formatDate(props.dataItem.lastLoginDate, true) }}</td>
        </template>
        <template #RemoveTemplate="{ props }">
          <td>
            <button
              class="
                  tw-px-2
                  tw-py-2
                  tw-border
                  tw-border-solid
                  tw-rounded
                  tw-leading-none
                  hover:tw-bg-connx-grey-tint-70
                "
              type="button"
              aria-label="Remove Device"
              @click="authenticationDevices.removeDevice(props.dataItem)"
            >
              Remove
            </button>
          </td>
        </template>
      </grid>
    </div>

    <div
      v-for="(device, index) in rememberedDeviceList"
      :key="index"
      class="tw-border-solid md:tw-hidden tw-rounded tw-border-connx-grey-tint-80 tw-my-2 tw-border tw-p-4 hover:tw-shadow-md"
    >
      <div
        class="tw-flex tw-cursor-pointer"
        @click="device.controlsVisible.value = !device.controlsVisible.value"
      >
        <div class="tw-mr-4 tw-w-14">
          <connx-icon
            v-if="device.isDesktopDevice"
            name="far fa-laptop"
            class="tw-w-12 tw-text-connx-grey-shade-20 tw-mt-2"
          />
          <connx-icon
            v-else
            class="tw-w-6 tw-mx-auto tw-text-connx-grey-shade-20 tw-mt-2"
            name="fas fa-mobile-alt"
          />
        </div>
        <div class="tw-w-full">
          <h4 class="tw-flex tw-items-center tw-justify-between">
            {{ device.deviceName }}
            <span
              v-if="device.isDeviceExpired"
              class="tw-text-sm tw-text-connx-danger tw-font-normal"
            >
              (Expired)
            </span>
            <span
              v-else-if="device.isCurrentDevice"
              class="tw-text-sm tw-text-connx-info tw-font-normal"
            >
              (your current session)
            </span>
          </h4>
          <p>{{ device.browserName }}</p>
          <div class="tw-flex tw-justify-between">
            <p class="tw-text-sm">
              Last login:
            </p>
            <span class="tw-text-sm tw-uppercase">{{ formatDate(device.lastLoginDate, true) }}</span>
          </div>
          <div
            v-show="device.controlsVisible.value"
            class="tw-flex tw-justify-between"
          >
            <p class="tw-text-sm">
              Trusted from:
            </p>
            <span class="tw-text-sm">{{ formatDate(device.trustedFrom, true) }}</span>
          </div>
          <div
            v-show="device.controlsVisible.value"
            class="tw-flex tw-justify-between"
          >
            <p class="tw-text-sm">
              Trusted to:
            </p>
            <span class="tw-text-sm">{{ formatDate(device.trustedTo, true) }}</span>
          </div>
        </div>
      </div>

      <div
        v-show="device.controlsVisible.value"
        class="tw-mt-4 tw-text-center"
      >
        <connx-button
          class="tw-mx-auto"
          button-style="danger-alt"
          icon="fas fa-trash-alt"
          icon-position="before"
          @click="authenticationDevices.removeDevice(device)"
        >
          Remove
        </connx-button>
      </div>
    </div>
  </div>
</template>
